/**
 * Route Detail Page - Stop-by-stop route execution
 *
 * Shows all Geplande_Rit__c for a route with completion animations,
 * sequence validation, chauffeur verification, and skip workflow.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, Button } from '../components/ui';
import { StopCard, StopCardWb, CompletedStopCard, NieuweStopWaarschuwing } from '../components/route';
import { NextStopBar, WbButton, WbFlag, WbModal, WbTextarea, WbTile } from '../components/wb';
import { createBericht } from '../lib/berichten';
import { ReparatieCargoSection } from '../components/route/ReparatieCargoSection';
import { RouteStartControle } from '../components/route/RouteStartControle';
import { useRouteStore, useHeader } from '../stores';
import { useSyncStatus, syncEngine } from '../lib/sync';
import { salesforceClient } from '../lib/salesforce';
import { decodeHtmlEntities, formatArrivalTime } from '../lib/utils';
import { isWerkbank } from '../lib/uiFlag';
import { moetVertrekMelden, vandaagAmsterdam } from '../lib/route-vertrek';
import {
  SKIP_REASONS,
  ATTEMPTED_ACTIONS,
} from '../lib/constants';
import type { GeplandeRitWithDetails, SkipReason, AttemptedAction } from '../lib/salesforce';

/** Cadans waarmee de stoplijst onderweg wordt ververst. */
const STOPS_POLL_MS = 120000;

/** Werkbank-render (plan 2026-09-02) of legacy; per toestel, zie lib/uiFlag. */
const WB = isWerkbank();
/**
 * Startwaarde voor de hoogte van de sticky routekop (px). De echte hoogte wordt
 * gemeten (ResizeObserver): de kop was 73 px terwijl hier 60 stond, waardoor de
 * NextStopBar en de eerste kaart 13 px onder de kop verdwenen (05-09).
 */
const WB_KOP_H_START = 60;
/** De app-header is sticky; de routekop plakt eronder, de NextStopBar daar weer onder. */
const WB_KOP_TOP = 'calc(var(--wb-header-h) + env(safe-area-inset-top, 0px))';

export function RouteDetailPage() {
  const { routeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we should expand a specific work order (e.g., after adding an item)
  const expandedWorkOrderId = (location.state as { expandWorkOrderId?: string })?.expandWorkOrderId;

  // Route store
  const {
    currentRoute,
    stops,
    setCurrentRoute,
    setStops,
    completeStop: storeCompleteStop,
    skipStop: storeSkipStop,
    partialPickupStop: storePartialPickup,
    heropenStop: storeHeropenStop,
    completedStopsCount,
    totalStopsCount,
  } = useRouteStore();

  // Sync status
  const { hasPendingSyncs, isSyncing, isOnline } = useSyncStatus();

  // Local UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSkipModal, setShowSkipModal] = useState<string | null>(null);
  const [skipReason, setSkipReason] = useState<SkipReason | null>(null);
  const [skipNotes, setSkipNotes] = useState('');
  const [attemptedActions, setAttemptedActions] = useState<AttemptedAction[]>([]);
  const [showChauffeurBlockModal, setShowChauffeurBlockModal] = useState<string | null>(null);
  const [showSequenceWarning, setShowSequenceWarning] = useState(false);
  const [pendingCompleteId, setPendingCompleteId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [heropenStopId, setHeropenStopId] = useState<string | null>(null);
  const [vertrekGecontroleerd, setVertrekGecontroleerd] = useState<string | null>(null);
  const vertrekGemeldVoor = useRef<string | null>(null);

  // Gemeten hoogte van de sticky routekop; de NextStopBar en de
  // IntersectionObserver-marge rekenen ermee.
  const kopRef = useRef<HTMLDivElement | null>(null);
  const [kopHoogte, setKopHoogte] = useState(WB_KOP_H_START);
  useEffect(() => {
    if (!WB) return;
    const el = kopRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const meet = () => setKopHoogte(Math.round(el.getBoundingClientRect().height));
    meet();
    const obs = new ResizeObserver(meet);
    obs.observe(el);
    return () => obs.disconnect();
  }, [isLoading, error]);
  const wbBarTop = `calc(var(--wb-header-h) + env(safe-area-inset-top, 0px) + ${kopHoogte}px)`;

  const sluitSkipModal = () => {
    setShowSkipModal(null);
    setSkipReason(null);
    setSkipNotes('');
    setAttemptedActions([]);
  };
  const sluitFinalizeModal = () => {
    setShowFinalizeModal(false);
    setFinalizeResult(null);
  };
  const handleFinalizeRoute = async () => {
    if (!routeId) return;
    setIsFinalizing(true);
    try {
      // Een stop-afmelding wordt lokaal meteen op Verwerkt gezet en pas daarna
      // via de wachtrij naar Salesforce geschreven. Finalize gaat er
      // rechtstreeks langs. Rondt de chauffeur af vlak na zijn laatste stop,
      // dan staan die kleden in Salesforce nog niet op Opgehaald en vallen ze
      // buiten de sweep — zo bleven 28-07 twaalf kleden van WB-308777 staan.
      // Dus eerst de wachtrij leeg.
      if (syncEngine.hasPendingSyncs()) {
        await syncEngine.forceSync();
      }
      if (syncEngine.hasPendingSyncs()) {
        const openstaand = syncEngine.getPendingSyncCount();
        setFinalizeResult({
          success: false,
          message: `${openstaand} afmelding${openstaand !== 1 ? 'en' : ''} ${openstaand !== 1 ? 'zijn' : 'is'} nog niet verstuurd. Zoek even verbinding en rond daarna opnieuw af.`,
        });
        return;
      }

      const result = await salesforceClient.finalizeRoute(routeId);
      if (result.success && result.data) {
        setFinalizeResult({ success: true, itemsUpdated: result.data.itemsUpdated, itemsOpgehaaldTotaal: result.data.itemsOpgehaaldTotaal });
      } else {
        setFinalizeResult({ success: false, message: result.message || 'Er is iets misgegaan' });
      }
    } catch (err) {
      setFinalizeResult({ success: false, message: err instanceof Error ? err.message : 'Er is iets misgegaan' });
    } finally {
      setIsFinalizing(false);
    }
  };
  const [finalizeResult, setFinalizeResult] = useState<{ success: boolean; itemsUpdated?: number; itemsOpgehaaldTotaal?: number; message?: string } | null>(null);

  // Ladingbevestiging op na-lock aflever-stops. Lokaal per route bewaard: de
  // chauffeur mag de app onderweg sluiten of de route opnieuw openen zonder
  // dezelfde vraag terug te krijgen. Bewust géén server-write — een "ja" is
  // geen gebeurtenis waar kantoor iets mee moet, een "nee" wél (dat wordt een
  // bericht). Zie NieuweStopWaarschuwing voor de afweging.
  const bevestigdSleutel = `kleda-field:nalock-bevestigd:${routeId ?? ''}`;
  const [ladingBevestigd, setLadingBevestigd] = useState<Set<string>>(() => {
    try {
      const rauw = localStorage.getItem(`kleda-field:nalock-bevestigd:${routeId ?? ''}`);
      return new Set(rauw ? (JSON.parse(rauw) as string[]) : []);
    } catch {
      return new Set();
    }
  });

  const bevestigLading = useCallback((stopId: string) => {
    setLadingBevestigd(prev => {
      const volgende = new Set(prev).add(stopId);
      try {
        localStorage.setItem(bevestigdSleutel, JSON.stringify([...volgende]));
      } catch {
        // Storage vol of geblokkeerd: de bevestiging geldt dan alleen deze sessie.
      }
      return volgende;
    });
  }, [bevestigdSleutel]);

  /**
   * Chauffeur heeft het kleed niet in de bus. Dit moet nú bij kantoor liggen,
   * niet vanavond: de klant verwacht vandaag een levering en er is nog tijd om
   * te bellen of het kleed alsnog te halen.
   */
  const meldOntbrekendeLading = useCallback(async (stop: GeplandeRitWithDetails) => {
    const wanneer = stop.addedAt ? ` (stop toegevoegd ${stop.addedAt})` : '';
    await createBericht({
      werkOrderId: stop.Werk_Order__c ?? null,
      werkOrderName: stop.workOrderName ?? null,
      customerName: stop.customerName ?? null,
      prioriteit: 'urgent',
      onderwerp: 'Kleed niet in de bus — stop na vergrendeling toegevoegd',
      body:
        `Stop ${stop.Route_Sequence__c ?? '?'} (${stop.customerName ?? 'klant'}, ` +
        `${stop.address ?? 'adres onbekend'}) is aan deze route toegevoegd nadat hij al ` +
        `vaststond${wanneer}. Het kleed zit niet in de bus, dus deze aflevering gaat vandaag ` +
        `niet lukken. Graag de klant bellen en opnieuw inplannen.`,
    });
  }, []);

  // Function to refresh stops (used after payment registration, and door de
  // poll hieronder). Staat er nog een afronding in de sync-wachtrij, dan lezen
  // we niet: de server kent die stop nog als Ingepland en zou de optimistische
  // stand terugdraaien. De volgende tick pakt het op zodra de wachtrij leeg is.
  const refreshStops = useCallback(async () => {
    if (!routeId) return;
    if (syncEngine.hasPendingSyncs()) return;

    setIsRefreshing(true);
    try {
      const stopsResponse = await salesforceClient.getRouteStops(routeId);
      if (stopsResponse.success && stopsResponse.data) {
        setStops(stopsResponse.data as GeplandeRitWithDetails[]);
      }
    } catch (err) {
      console.error('Error refreshing stops:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [routeId, setStops]);

  // Fetch route and stops from API
  useEffect(() => {
    async function fetchRouteData() {
      if (!routeId) return;

      setIsLoading(true);
      setError(null);

      try {
        // First get routes to find the route details
        const todayStr = new Date().toISOString().split('T')[0]!;
        const routesResponse = await salesforceClient.getDriverRoutes(todayStr);

        if (routesResponse.success && routesResponse.data) {
          const routes = routesResponse.data as unknown as Array<{
            Id: string;
            Name: string;
            Routenaam_voor_Kalenderweergave__c: string;
            Chauffeur__c: string;
            Datum__c: string;
            totalStops: number;
            completedStops: number;
          }>;

          const foundRoute = routes.find(r => r.Id === routeId);
          if (foundRoute) {
            setCurrentRoute({
              Id: foundRoute.Id,
              Name: foundRoute.Name,
              Routenaam_voor_Kalenderweergave__c: foundRoute.Routenaam_voor_Kalenderweergave__c,
              Chauffeur__c: foundRoute.Chauffeur__c,
              Datum__c: foundRoute.Datum__c,
            });
          }
        }

        // Get the stops for this route
        const stopsResponse = await salesforceClient.getRouteStops(routeId);

        if (stopsResponse.success && stopsResponse.data) {
          console.log('[RouteDetailPage] Stops API response:', stopsResponse.data);
          // Log isKlacht field for debugging
          (stopsResponse.data as GeplandeRitWithDetails[]).forEach(stop => {
            console.log(`[RouteDetailPage] Stop ${stop.Name}: isKlacht=${stop.isKlacht}, isSpoed=${stop.isSpoed}`);
          });
          setStops(stopsResponse.data as GeplandeRitWithDetails[]);
        } else {
          setError(stopsResponse.message || 'Kon stops niet laden');
        }
      } catch (err) {
        console.error('Error fetching route data:', err);
        setError('Er is iets misgegaan bij het laden van de route');
      } finally {
        setIsLoading(false);
      }
    }

    fetchRouteData();
  }, [routeId, setCurrentRoute, setStops]);

  // Stops verversen tijdens de rit. De lijst werd tot nu toe één keer geladen
  // bij het openen; een stop die kantoor er onderweg bij zet (en die de
  // inpasser binnen ~10 min op zijn plek zet) bereikte de chauffeur pas als
  // hij de route opnieuw opende (casus WB-323220, 02-09). Elke 2 minuten,
  // plus bij terugkeer naar de app, zelfde cadans als de inpakken-store.
  useEffect(() => {
    if (!routeId) return;
    const bijZichtbaar = () => {
      if (document.visibilityState === 'visible') void refreshStops();
    };
    const timer = setInterval(bijZichtbaar, STOPS_POLL_MS);
    document.addEventListener('visibilitychange', bijZichtbaar);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', bijZichtbaar);
    };
  }, [routeId, refreshStops]);

  // Calculate progress from store or local state
  const progressPercent = totalStopsCount > 0 ? Math.round((completedStopsCount / totalStopsCount) * 100) : 0;

  // Get pending (not completed) stops
  const pendingStops = stops.filter(s => s.Status__c === 'Ingepland' && !s.Overgeslagen__c);
  const completedStopsList = stops.filter(s => s.Status__c === 'Verwerkt' || s.Overgeslagen__c);
  // Herijkte tijden (plan 2026-10-12): zodra één stop een werkelijk afrondmoment
  // heeft, zijn de tijden van de rest live en heten ze "verwacht".
  const tijdenLive = stops.some(s => !!s.verwerktOp);

  // Get next expected stop. Een stop zonder sequence (na de lock toegevoegd,
  // inpasser nog niet geweest) sorteert vooraan maar heeft nog geen plek in de
  // rit; die is dus niet "de volgende", ook al staat hij bovenaan de lijst.
  const nextExpectedStop = pendingStops.find(s => s.Route_Sequence__c != null) ?? pendingStops[0];

  // Stops die ná het vergrendelen van de route zijn aangeplakt. Die zijn voor
  // de chauffeur nieuw en staan meestal achteraan, dus visueel onzichtbaar
  // tussen de rest (casus WB-322910). Alleen de nog te rijden stops: een al
  // afgeronde stop hoeft geen waarschuwing meer.
  const nieuweStops = pendingStops.filter(s => s.isAddedAfterLock);

  // Vertrek melden (plan 2026-10-12, Decision 16). Niet op het moment van de
  // vertrekcontrole: die doen chauffeurs ook bij het laden de avond ervoor, en
  // de office-app weigert een vertrek buiten de routedag (VERTREK_NIET_OP_ROUTEDAG).
  // Dus zodra de gecontroleerde route op de routedag in beeld is: bij openen en
  // als de app 's ochtends weer naar de voorgrond komt. Eén keer per route per
  // dag (lib/route-vertrek.ts); offline mist alleen het depot-anker, de eerste
  // afgeronde stop neemt het dan over.
  const routeDatum = currentRoute?.Datum__c ?? null;
  const aantalAfgerond = completedStopsList.length;
  const aantalOpen = pendingStops.length;
  const meldVertrek = useCallback(() => {
    const sleutel = moetVertrekMelden({
      routeId: routeId ?? null, routeDatum, vandaag: vandaagAmsterdam(), gecontroleerd: vertrekGecontroleerd,
      aantalAfgerond, aantalOpen, alGemeldVoor: vertrekGemeldVoor.current,
    });
    if (!sleutel || !routeId) return;
    vertrekGemeldVoor.current = sleutel;
    salesforceClient.vertrekRoute(routeId, new Date().toISOString()).catch((err) => {
      vertrekGemeldVoor.current = null;
      console.warn('[RouteDetailPage] vertrek melden op de routedag mislukt (herijk start bij de eerste stop):', err);
    });
  }, [routeId, routeDatum, vertrekGecontroleerd, aantalAfgerond, aantalOpen]);
  useEffect(() => { meldVertrek(); }, [meldVertrek]);
  useEffect(() => {
    const bijZichtbaar = () => { if (document.visibilityState === 'visible') meldVertrek(); };
    document.addEventListener('visibilitychange', bijZichtbaar);
    return () => document.removeEventListener('visibilitychange', bijZichtbaar);
  }, [meldVertrek]);

  // Werkbank-shell: routenaam in de app-header, teller ernaast.
  useHeader(
    WB ? (currentRoute?.Routenaam_voor_Kalenderweergave__c || 'Route') : null,
    WB ? `${completedStopsCount}/${totalStopsCount}` : null
  );

  // NextStopBar: zichtbaar zolang de kaart van de volgende stop niet in beeld
  // is. IntersectionObserver op `#stop-<Id>`; bij elke wissel van de volgende
  // stop (afronden, verversen) opnieuw aanhaken.
  const [nextInView, setNextInView] = useState(true);
  const nextStopId = nextExpectedStop?.Id ?? null;
  useEffect(() => {
    if (!WB || !nextStopId) return;
    const el = document.getElementById(`stop-${nextStopId}`);
    if (!el || typeof IntersectionObserver === 'undefined') {
      setNextInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => setNextInView(entries.some((e) => e.isIntersecting)),
      // Kop + balk staan boven de content; een kaart die daaronder verdwijnt telt als uit beeld.
      { rootMargin: `-${56 + kopHoogte + 56}px 0px 0px 0px`, threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [nextStopId, isLoading, kopHoogte]);

  const nextStopBarStop = WB && nextExpectedStop
    ? (() => {
        const adres = decodeHtmlEntities(nextExpectedStop.address || nextExpectedStop.Address__c || '')
          .replace(/<br\s*\/?>/gi, '\n').replace(/[ \t]+/g, ' ').trim();
        const straat = adres.split('\n')[0]?.trim() || '';
        return {
          nummer: nextExpectedStop.Route_Sequence__c ?? '?',
          naam: nextExpectedStop.customerName || nextExpectedStop.workOrderName || 'Stop',
          straat,
          eta: formatArrivalTime(nextExpectedStop.Arrival_Time_D_t__c),
          afstand: nextExpectedStop.Kilometer__c ? String(nextExpectedStop.Kilometer__c) : null,
          minuten: nextExpectedStop.Hour_And_Minutes__c ? String(nextExpectedStop.Hour_And_Minutes__c) : null,
          type: nextExpectedStop.Type__c,
        };
      })()
    : null;

  // Check if completing out of sequence
  const isOutOfSequence = useCallback((ritId: string) => {
    const stop = stops.find(s => s.Id === ritId);
    if (!stop || !nextExpectedStop) return false;
    return (stop.Route_Sequence__c ?? 0) > (nextExpectedStop.Route_Sequence__c ?? 0);
  }, [stops, nextExpectedStop]);

  // Check chauffeur ownership
  // With PIN auth, security is enforced server-side (Apex filters by Medewerker__c).
  // The Chauffeur__c field is a legacy User lookup that doesn't match the staff UUID,
  // so we skip the frontend check. The server already ensures only your stops are returned.
  const checkChauffeurMismatch = useCallback((_stop: GeplandeRitWithDetails): boolean => {
    return false;
  }, []);

  // Check if stop can be completed (returns true if OK, false if blocked/warning needed)
  const checkCanComplete = useCallback((ritId: string): 'ok' | 'chauffeur_mismatch' | 'out_of_sequence' => {
    const stop = stops.find(s => s.Id === ritId);
    if (!stop) return 'ok';

    if (checkChauffeurMismatch(stop)) {
      return 'chauffeur_mismatch';
    }

    if (isOutOfSequence(ritId)) {
      return 'out_of_sequence';
    }

    return 'ok';
  }, [stops, checkChauffeurMismatch, isOutOfSequence]);

  // Handle complete stop
  const handleCompleteStop = useCallback(async (ritId: string, force = false) => {
    const stop = stops.find(s => s.Id === ritId);
    if (!stop) return;

    // Check chauffeur mismatch - hard block
    if (checkChauffeurMismatch(stop)) {
      setShowChauffeurBlockModal(ritId);
      return;
    }

    // Check sequence - warn but allow to proceed
    if (!force && isOutOfSequence(ritId)) {
      setPendingCompleteId(ritId);
      setShowSequenceWarning(true);
      return;
    }

    // Use store action for optimistic update + sync queue
    storeCompleteStop(ritId, stop.Type__c || 'Ophalen');

    // Trigger haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }

    // Clear any pending state
    setPendingCompleteId(null);
    setShowSequenceWarning(false);
  }, [stops, checkChauffeurMismatch, isOutOfSequence, storeCompleteStop]);

  // Handle skip stop
  const handleSkipStop = useCallback(async () => {
    if (!showSkipModal || !skipReason) return;

    // Use store action for optimistic update + sync queue
    // Pass attemptedActions only for 'Niet thuis' or 'Geen toegang'
    const actionsToSend = (skipReason === 'Niet thuis' || skipReason === 'Geen toegang')
      ? attemptedActions
      : undefined;
    storeSkipStop(showSkipModal, skipReason, actionsToSend, skipNotes.trim() || undefined);

    // Reset modal state
    setShowSkipModal(null);
    setSkipReason(null);
    setSkipNotes('');
    setAttemptedActions([]);
  }, [showSkipModal, skipReason, attemptedActions, skipNotes, storeSkipStop]);

  // Open Google Maps for navigation
  const openNavigation = (address: string) => {
    // Clean the address: decode SF entity-encoded chars, then strip HTML tags and normalize whitespace
    const cleanAddress = decodeHtmlEntities(address)
      .replace(/<[^>]*>/g, ' ')  // Remove all HTML tags (including <br>, <br/>, etc.)
      .replace(/\s+/g, ' ')  // Normalize multiple spaces
      .trim();

    if (!cleanAddress) {
      console.warn('No address to navigate to');
      return;
    }

    const encodedAddress = encodeURIComponent(cleanAddress);
    // Use geo: URI for mobile, fallback to maps URL for desktop
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const url = isMobile
      ? `geo:0,0?q=${encodedAddress}`
      : `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;

    console.log('[Navigation] Cleaned address:', cleanAddress);
    console.log('[Navigation] Generated URL:', url);

    window.open(url, '_blank');
  };

  // Open phone for calling
  const openPhone = (phoneNumber: string) => {
    window.location.href = `tel:${phoneNumber}`;
  };
  const telefoonVan = (stop: GeplandeRitWithDetails) =>
    stop.phoneNumber || stop.Werk_Order__r?.Mobiel__c || stop.Werk_Order__r?.Telefoon__c || '';

  // Heropenen: overgeslagen stop terug naar "te doen" (klant belde terug).
  const heropenStop = stops.find((s) => s.Id === heropenStopId) ?? null;
  const handleHeropenStop = () => {
    if (!heropenStopId) return;
    storeHeropenStop(heropenStopId);
    setHeropenStopId(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-kleda-bg-clean flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-kleda-primary border-t-transparent rounded-full animate-spin mx-auto mb-md" />
          <p className="text-kleda-text-muted">Route laden...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-kleda-bg-clean p-lg">
        <Card variant="elevated">
          <CardContent>
            <div className="text-center py-lg">
              <div className="w-16 h-16 mx-auto mb-md bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="font-heading font-bold text-lg text-kleda-text-main mb-xs">
                Kon route niet laden
              </h3>
              <p className="text-kleda-text-muted mb-lg">{error}</p>
              <Button onClick={() => navigate('/routes')}>
                Terug naar overzicht
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // De controle geldt ook bij een directe link naar de route. De knop 'Start
  // route' was alleen navigatie en voerde geen enkele laadcontrole uit.
  if (routeId && pendingStops.length > 0 && completedStopsList.length === 0 && vertrekGecontroleerd !== routeId) {
    return <RouteStartControle key={routeId} routeId={routeId} stopIds={pendingStops.map(s => s.Id)}
      onReady={() => {
        // Het vertrek zelf meldt het effect hierboven, en alleen op de routedag.
        setVertrekGecontroleerd(routeId);
      }} onCancel={() => navigate('/routes')} />;
  }

  const StopCardRender = WB ? StopCardWb : StopCard;

  return (
    <div className={WB ? 'min-h-screen wb-body -mx-3 -mt-3' : 'min-h-screen bg-kleda-bg-clean'}>
      {/* Sticky header — Werkbank: plat vlak met 2 px onderrand, teller mono */}
      <div
        ref={kopRef}
        className={WB ? 'sticky z-10 bg-wb-vlak border-b-wb border-wb-rand' : 'sticky top-0 z-10 bg-white shadow-sm'}
        style={WB ? { top: WB_KOP_TOP } : undefined}
      >
        <div className={WB ? 'px-3 pt-2 pb-2' : 'px-lg py-md'}>
          {/* Back button and title */}
          <div className={WB ? 'flex items-center gap-2 mb-1.5' : 'flex items-center gap-md mb-sm'}>
            <button
              onClick={() => navigate('/routes')}
              aria-label="Terug naar routes"
              className={WB ? 'wb-focus min-h-tap min-w-[52px] -ml-2 flex items-center justify-center rounded-wb' : 'p-sm -ml-sm rounded-full hover:bg-kleda-bg-clean transition-colors'}
            >
              <svg className={WB ? 'w-7 h-7 text-wb-inkt' : 'w-6 h-6 text-kleda-text-main'} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={WB ? 2.5 : 2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex-1 min-w-0">
              <h1 className={WB ? 'font-wb-heading font-bold text-lg text-wb-inkt truncate leading-tight' : 'font-heading font-bold text-lg text-kleda-text-main truncate'}>
                {currentRoute?.Routenaam_voor_Kalenderweergave__c || 'Route'}
              </h1>
              <div className="flex items-center gap-xs">
                <p className={WB ? 'font-mono text-sm font-semibold text-wb-inkt' : 'text-sm text-kleda-text-muted'}>
                  {WB ? `${completedStopsCount}/${totalStopsCount} stops` : `${completedStopsCount} van ${totalStopsCount} stops`}
                </p>
                {/* Sync indicator */}
                {!isOnline && (
                  <span className="inline-flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 bg-orange-500 rounded-full" />
                    Offline
                  </span>
                )}
                {hasPendingSyncs && isOnline && (
                  <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                    <span className={`w-1.5 h-1.5 bg-blue-500 rounded-full ${isSyncing ? 'animate-pulse' : ''}`} />
                    {isSyncing ? 'Syncing...' : 'Pending'}
                  </span>
                )}
                {isRefreshing && (
                  <span className="inline-flex items-center gap-1 text-xs text-kleda-primary bg-kleda-primary/10 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 bg-kleda-primary rounded-full animate-pulse" />
                    Updaten...
                  </span>
                )}
                {/* Media-laag: de wachtende uploads staan sinds 17-08 in de
                    app-header (MediaUploadTeller), dus op élke pagina en niet
                    alleen hier. Eén teller, één implementatie. */}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className={WB ? 'h-2 bg-wb-gedempt overflow-hidden' : 'h-2 bg-gray-200 rounded-full overflow-hidden'}>
            <div
              className={WB ? 'h-full bg-wb-inkt transition-all duration-500' : 'h-full bg-kleda-primary transition-all duration-500 rounded-full'}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Werkbank: de volgende stop altijd in beeld zodra zijn kaart uit beeld scrolt */}
      {WB && nextStopId && (
        <NextStopBar
          stop={nextStopBarStop}
          targetId={`stop-${nextStopId}`}
          visible={!nextInView}
          top={wbBarTop}
        />
      )}

      {/* Stops list */}
      <div className={WB ? 'px-3 pt-5 pb-3 space-y-3' : 'px-lg py-md space-y-md'}>
        {/* Stops die na het vergrendelen zijn aangeplakt — bovenaan, want ze
            staan in de lijst zelf meestal achteraan en vallen daar niet op. */}
        <NieuweStopWaarschuwing
          stops={nieuweStops}
          bevestigd={ladingBevestigd}
          onBevestig={bevestigLading}
          onMeldOntbrekend={meldOntbrekendeLading}
        />

        {/* Pending stops */}
        {pendingStops.length > 0 && (
          <section>
            <h2 className={WB ? 'font-wb-heading font-bold text-xs text-wb-inkt2 uppercase tracking-wide mb-2' : 'font-heading font-bold text-sm text-kleda-text-muted uppercase tracking-wide mb-sm'}>
              Te doen ({pendingStops.length})
            </h2>
            <div className={WB ? 'space-y-3' : 'space-y-sm'}>
              {pendingStops.map((stop) => {
                if (stop.isReparateurStop) {
                  return (
                    <ReparatieCargoSection
                      key={stop.Id}
                      stop={stop}
                      isNext={stop.Id === nextExpectedStop?.Id}
                      onComplete={() => handleCompleteStop(stop.Id)}
                      onNavigate={() => openNavigation(
                        stop.address || stop.Address__c || ''
                      )}
                      onCall={() => openPhone(stop.phoneNumber || '')}
                      onCargoChanged={refreshStops}
                    />
                  );
                }
                // Gemengde stop: klantorder op een reparateur-account met
                // gekoppelde reparatie-kleden. StopCard blijft leidend
                // (afronden, betaling, WO-flow); de embedded cargo-sectie
                // toont de reparatie-kleden met afgeleverd/opgehaald-acties.
                const reparatieExtra = stop.heeftReparatieLading ? (
                  <ReparatieCargoSection
                    stop={stop}
                    isNext={false}
                    embedded
                    onComplete={() => {}}
                    onNavigate={() => {}}
                    onCall={() => {}}
                    onCargoChanged={refreshStops}
                  />
                ) : null;
                return (
                  <div key={stop.Id} className="space-y-sm">
                  <StopCardRender
                    stop={stop}
                    tijdenLive={tijdenLive}
                    isNext={stop.Id === nextExpectedStop?.Id}
                    onComplete={() => handleCompleteStop(stop.Id)}
                    onSkip={() => setShowSkipModal(stop.Id)}
                    onPartialPickup={(pickedUpItemIds, notes, reason) =>
                      storePartialPickup(stop.Id, pickedUpItemIds, notes, reason)
                    }
                    onNavigate={() => openNavigation(
                      stop.Address__c || stop.address ||
                      (stop.Type__c === 'Ophalen' ? stop.Werk_Order__r?.Ophaaladres_formule__c : stop.Werk_Order__r?.Afleveradres_formule__c) || ''
                    )}
                    onCall={() => openPhone(
                      stop.phoneNumber || stop.Werk_Order__r?.Mobiel__c || stop.Werk_Order__r?.Telefoon__c || ''
                    )}
                    isChauffeurMismatch={checkChauffeurMismatch(stop)}
                    initiallyExpanded={expandedWorkOrderId === stop.Werk_Order__c}
                    onPaymentUpdated={(updatedPayment) => {
                      const updatedStops = stops.map(s =>
                        s.Id === stop.Id
                          ? { ...s, ...updatedPayment }
                          : s
                      );
                      setStops(updatedStops);
                    }}
                    onRefreshRoute={refreshStops}
                    checkCanComplete={() => checkCanComplete(stop.Id)}
                    onShowSequenceWarning={() => {
                      setPendingCompleteId(stop.Id);
                      setShowSequenceWarning(true);
                    }}
                    onShowChauffeurBlock={() => setShowChauffeurBlockModal(stop.Id)}
                  />
                  {reparatieExtra}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Completed stops */}
        {completedStopsList.length > 0 && (
          <section>
            <h2 className={WB ? 'font-wb-heading font-bold text-xs text-wb-inkt2 uppercase tracking-wide mb-2' : 'font-heading font-bold text-sm text-kleda-text-muted uppercase tracking-wide mb-sm'}>
              Voltooid ({completedStopsList.length})
            </h2>
            <div className={WB ? 'space-y-1.5' : 'space-y-sm opacity-60'}>
              {completedStopsList.map(stop => (
                <CompletedStopCard
                  key={stop.Id}
                  stop={stop}
                  onCall={telefoonVan(stop) ? () => openPhone(telefoonVan(stop)) : undefined}
                  onHeropen={stop.Overgeslagen__c ? () => setHeropenStopId(stop.Id) : undefined}
                />
              ))}
            </div>
          </section>
        )}

        {/* All done message */}
        {pendingStops.length === 0 && (
          <Card variant="elevated">
            <CardContent>
              <div className="text-center py-lg">
                <div className="w-16 h-16 mx-auto mb-md bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="font-heading font-bold text-lg text-kleda-text-main mb-xs">
                  Route voltooid!
                </h3>
                <p className="text-kleda-text-muted mb-lg">
                  Alle {totalStopsCount} stops zijn afgerond.
                </p>

                {/* Finalize route button - only show for routes with pickups */}
                {stops.some(s => s.Type__c === 'Ophalen' && s.Status__c === 'Verwerkt' && !s.Overgeslagen__c) && (
                  <div className="space-y-sm mb-lg">
                    <Button
                      fullWidth
                      onClick={() => setShowFinalizeModal(true)}
                      className="bg-kleda-primary hover:bg-kleda-primary-dark"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                      Route afronden
                    </Button>
                    <p className="text-xs text-kleda-text-muted">
                      Zet alle opgehaalde items klaar voor voorinspectie
                    </p>
                  </div>
                )}

                <Button variant="outline" onClick={() => navigate('/routes')}>
                  Terug naar overzicht
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Overslaan: reden als tegels, geprobeerde acties als tegels, notitie */}
      <WbModal
        open={showSkipModal !== null}
        onClose={sluitSkipModal}
        titel="Stop overslaan"
        sluitOpAchtergrond={!skipReason}
        acties={
          <>
            <WbButton variant="secondary" size="lg" onClick={sluitSkipModal}>Annuleren</WbButton>
            <WbButton variant="danger" size="lg" disabled={!skipReason || (skipReason === 'Anders' && !skipNotes.trim())} onClick={handleSkipStop}>Overslaan</WbButton>
          </>
        }
      >
        <div className="space-y-4">
          <section className="space-y-1.5">
            <h3 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">Reden</h3>
            <div className="grid grid-cols-2 gap-1.5">
              {SKIP_REASONS.map((reason) => (
                <WbTile key={reason.value} rol="radio" selected={skipReason === reason.value} onToggle={() => setSkipReason(reason.value)}>{reason.label}</WbTile>
              ))}
            </div>
          </section>

          {/* Geprobeerde acties bij 'Niet thuis' of 'Geen toegang' */}
          {(skipReason === 'Niet thuis' || skipReason === 'Geen toegang') && (
            <section className="space-y-1.5">
              <h3 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">Wat heb je geprobeerd?</h3>
              <div className="grid grid-cols-2 gap-1.5">
                {ATTEMPTED_ACTIONS.map((action) => (
                  <WbTile
                    key={action.value}
                    selected={attemptedActions.includes(action.value)}
                    onToggle={() =>
                      setAttemptedActions(
                        attemptedActions.includes(action.value)
                          ? attemptedActions.filter((a) => a !== action.value)
                          : [...attemptedActions, action.value]
                      )
                    }
                  >
                    {action.label}
                  </WbTile>
                ))}
              </div>
            </section>
          )}

          {/* Notitieveld voor alle niet-afwezigheidsredenen; verplicht bij 'Anders' */}
          {skipReason && skipReason !== 'Niet thuis' && skipReason !== 'Geen toegang' && (
            <WbTextarea
              label={skipReason === 'Anders' ? 'Reden (verplicht)' : 'Toelichting voor kantoor (optioneel)'}
              value={skipNotes}
              onChange={(e) => setSkipNotes(e.target.value)}
              placeholder={skipReason === 'Anders' ? 'Beschrijf de reden…' : 'Toelichting…'}
              rows={3}
            />
          )}
        </div>
      </WbModal>

      {/* Heropenen: overgeslagen stop terug naar te doen */}
      <WbModal
        open={heropenStopId !== null}
        onClose={() => setHeropenStopId(null)}
        titel={heropenStop?.Type__c === 'Terugbrengen' ? 'Toch afleveren?' : 'Toch ophalen?'}
        acties={
          <>
            <WbButton variant="secondary" size="lg" onClick={() => setHeropenStopId(null)}>Annuleren</WbButton>
            <WbButton size="lg" onClick={handleHeropenStop}>Terug naar te doen</WbButton>
          </>
        }
      >
        <div className="space-y-2">
          <p className="text-base text-wb-inkt2">
            <strong className="text-wb-inkt">{heropenStop?.customerName ?? 'Deze stop'}</strong> komt terug in je lijst
            en rond je daarna gewoon af.
          </p>
          {(heropenStop?.Reden_Overslaan__c === 'Niet thuis' || heropenStop?.Reden_Overslaan__c === 'Geen toegang') && (
            <WbFlag kind="opmerking">De klant heeft al een niet-thuis-mail gekregen; kantoor ziet in de order dat je alsnog langs bent geweest.</WbFlag>
          )}
        </div>
      </WbModal>

      {/* Andere chauffeur: geblokkeerd */}
      <WbModal
        open={showChauffeurBlockModal !== null}
        onClose={() => setShowChauffeurBlockModal(null)}
        titel="Andere chauffeur"
        acties={<WbButton size="lg" onClick={() => setShowChauffeurBlockModal(null)}>Begrepen</WbButton>}
      >
        <p className="text-base text-wb-inkt2">
          Deze stop is toegewezen aan{' '}
          <strong className="text-wb-inkt">{stops.find((s) => s.Id === showChauffeurBlockModal)?.Chauffeur__r?.Name || 'een andere chauffeur'}</strong>.
          Neem contact op met planning.
        </p>
      </WbModal>

      {/* Volgorde-waarschuwing: eerdere stops worden overgeslagen */}
      <WbModal
        open={showSequenceWarning && pendingCompleteId !== null}
        onClose={() => { setShowSequenceWarning(false); setPendingCompleteId(null); }}
        titel="Stop overslaan?"
        acties={
          <>
            <WbButton variant="secondary" size="lg" onClick={() => { setShowSequenceWarning(false); setPendingCompleteId(null); }}>Annuleren</WbButton>
            <WbButton size="lg" onClick={() => pendingCompleteId && handleCompleteStop(pendingCompleteId, true)}>Doorgaan</WbButton>
          </>
        }
      >
        <p className="text-base text-wb-inkt2">Je slaat hiermee eerdere stops over. Weet je zeker dat je door wilt gaan?</p>
      </WbModal>

      {/* Route afronden: bevestigen, resultaat, fout */}
      <WbModal
        open={showFinalizeModal}
        onClose={sluitFinalizeModal}
        sluitbaar={!isFinalizing}
        sluitOpAchtergrond={false}
        titel={!finalizeResult ? 'Route afronden?' : finalizeResult.success ? 'Route afgerond' : 'Afronden mislukt'}
        acties={
          !finalizeResult ? (
            <>
              <WbButton variant="secondary" size="lg" onClick={sluitFinalizeModal} disabled={isFinalizing}>Annuleren</WbButton>
              <WbButton size="lg" onClick={handleFinalizeRoute} isLoading={isFinalizing}>Afronden</WbButton>
            </>
          ) : finalizeResult.success ? (
            <WbButton size="lg" onClick={() => { sluitFinalizeModal(); navigate('/routes'); }}>Naar overzicht</WbButton>
          ) : (
            <>
              <WbButton variant="secondary" size="lg" onClick={sluitFinalizeModal}>Sluiten</WbButton>
              <WbButton size="lg" onClick={() => setFinalizeResult(null)}>Opnieuw proberen</WbButton>
            </>
          )
        }
      >
        {!finalizeResult ? (
          <div className="space-y-2">
            <p className="text-base text-wb-inkt2">Alle opgehaalde items worden klaargezet voor voorinspectie.</p>
            <WbFlag kind="opmerking">Doe dit pas als de bus is uitgeladen in de wasserij.</WbFlag>
          </div>
        ) : finalizeResult.success ? (
          <div className="space-y-1 text-base text-wb-inkt2">
            {/* Totaal voorop (werkbank #102): de stop-afmelding zet kleden vaak al door,
                dus "6 klaargezet" las als "15 kwijt" terwijl alle 21 goed stonden. */}
            {typeof finalizeResult.itemsOpgehaaldTotaal === 'number' && finalizeResult.itemsOpgehaaldTotaal > 0 ? (
              <>
                <p><span className="font-mono font-semibold text-wb-inkt">{finalizeResult.itemsOpgehaaldTotaal}</span> kle{finalizeResult.itemsOpgehaaldTotaal !== 1 ? 'den' : 'ed'} van deze route {finalizeResult.itemsOpgehaaldTotaal !== 1 ? 'staan' : 'staat'} in de wasserij.</p>
                <p className="text-sm text-wb-inkt3">
                  {finalizeResult.itemsUpdated === 0
                    ? 'Alles stond al klaar voor voorinspectie.'
                    : `${finalizeResult.itemsUpdated} ${finalizeResult.itemsUpdated !== 1 ? 'zijn' : 'is'} nu klaargezet voor voorinspectie, de rest stond al klaar.`}
                </p>
              </>
            ) : finalizeResult.itemsUpdated === 0
              ? <p>Geen items om te verwerken.</p>
              : <p><span className="font-mono font-semibold text-wb-inkt">{finalizeResult.itemsUpdated}</span> item{finalizeResult.itemsUpdated !== 1 ? 's' : ''} klaargezet voor voorinspectie.</p>}
          </div>
        ) : (
          <WbFlag kind="geld">{finalizeResult.message}</WbFlag>
        )}
      </WbModal>
    </div>
  );
}
